// VOY-485 (rebuild) — Recent purchases (Public profile tab).
//
// Source: `getRecentOrders()` — direct Supabase read of `orders` + nested
// `order_items` (no /api endpoint exists for this shape; the existing
// `purchases.tsx` page does the same direct read). Shows the 5 most
// recent orders regardless of status (the user wants to see in-flight
// work too).
//
// `View all` deep-links to `/profile/purchases` — the existing receipt
// viewer (functional but previously route-orphaned). The route is
// registered in App.tsx as part of this PR.
//
// SENSITIVE DATA RULE: payment details never displayed. We render
// destination/hotel + status + total + currency; we do NOT render
// customer_email, customer_name, stripe ids — those are admin-only
// fields and stay on the full /profile/purchases viewer.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plane, Building, Package } from 'lucide-react'
import { getRecentOrders, type RecentOrder, type RecentOrderItem } from '@/lib/profile'

export interface RecentPurchasesSectionProps {
  userId: string
}

function iconFor(item: RecentOrderItem) {
  if (item.item_type === 'flight') return <Plane aria-hidden="true" />
  if (item.item_type === 'hotel') return <Building aria-hidden="true" />
  return <Package aria-hidden="true" />
}

function destinationFor(item: RecentOrderItem): string {
  const d = item.item_data as { destination?: string; hotel_name?: string } | null
  if (!d) return item.item_type
  return d.hotel_name || d.destination || item.item_type
}

function statusClass(status: string | null): 'pill mint' | 'pill' {
  if (status === 'confirmed' || status === 'completed') return 'pill mint'
  return 'pill'
}

function fmtMoney(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null) return '—'
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount)
  } catch {
    return `${amount} ${currency || ''}`.trim()
  }
}

export function RecentPurchasesSection({ userId }: RecentPurchasesSectionProps) {
  const [orders, setOrders] = useState<RecentOrder[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    void (async () => {
      setLoading(true)
      try {
        setOrders(await getRecentOrders(userId, 5))
      } finally {
        setLoading(false)
      }
    })()
  }, [userId])

  return (
    <section className="card" aria-labelledby="purchases-h">
      <div className="card-h">
        <h3 id="purchases-h">Recent purchases</h3>
        <div className="row" style={{ gap: 12 }}>
          <span className="meta">{loading ? 'LOADING…' : 'LATEST 5'}</span>
          <Link to="/profile/purchases" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 11.5 }}>
            View all
          </Link>
        </div>
      </div>
      {!loading && orders.length === 0 ? (
        <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>
          No purchases yet.
        </p>
      ) : (
        <ul className="recent-list">
          {orders.map((order) => {
            const firstItem = order.order_items?.[0]
            return (
              <li key={order.id}>
                <Link to="/profile/purchases" className="recent-row">
                  <span className="recent-icon">{firstItem ? iconFor(firstItem) : <Package aria-hidden="true" />}</span>
                  <span className="recent-summary">
                    {firstItem ? destinationFor(firstItem) : `Order #${order.id.slice(0, 8)}`}
                    {order.order_items.length > 1 && (
                      <span style={{ color: 'var(--v6-fg-muted)', marginLeft: 6 }}>+{order.order_items.length - 1} more</span>
                    )}
                  </span>
                  <span className={statusClass(order.status)} style={{ marginRight: 10 }}>
                    {(order.status || '—').replace(/_/g, ' ')}
                  </span>
                  <span className="recent-date num">{fmtMoney(order.total_amount, order.currency)}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
